import l from '../../../../lang';
import { CreatePayoutData, FlowType, PurposeOfPayout, RequiredField } from '../../../../store/payouts/interfaces';

export const payoutCountryOptions: Record<string, string> = {
  AR: 'Argentina',
  BR: 'Brazil',
  CL: 'Chile',
  EC: 'Ecuador',
  MX: 'Mexico',
  PE: 'Peru',
  PY: 'Paraguay',
  UY: 'Uruguay',
};

export const payoutCurrencyOptionsByCountry: Record<string, Record<string, string>> = {
  AR: {
    ARS: 'ARS',
    USD: 'USD',
  },
  BR: {
    BRL: 'BRL',
    USD: 'USD',
  },
  CL: {
    CLP: 'CLP',
    USD: 'USD',
  },
  CR: {
    CRC: 'CRC',
    USD: 'USD',
  },
  DO: {
    DOP: 'DOP',
    USD: 'USD',
  },
  EC: {
    USD: 'USD',
  },
  MX: {
    MXN: 'MXN',
    USD: 'USD',
  },
  PE: {
    PEN: 'PEN',
    USD: 'USD',
  },
  PY: {
    PYG: 'PYG',
    USD: 'USD',
  },
  UY: {
    UYU: 'UYU',
    USD: 'USD',
  },
};

export const payoutPurposeOptions: Record<PurposeOfPayout, string> = {
  [PurposeOfPayout.FAMILY_MAINTENANCE]: l('payouts.create.purpose.familyMaintenance'),
  [PurposeOfPayout.MEDICAL_CARE]: l('payouts.create.purpose.medicalCare'),
  [PurposeOfPayout.TUITION_COSTS]: l('payouts.create.purpose.tuitionCosts'),
  [PurposeOfPayout.CHARITY_DONATION]: l('payouts.create.purpose.charityDonation'),
  [PurposeOfPayout.PROPERTY_PURCHASE]: l('payouts.create.purpose.propertyPurchase'),
  [PurposeOfPayout.SHARES]: l('payouts.create.purpose.shares'),
  [PurposeOfPayout.INVESTMENT]: l('payouts.create.purpose.investment'),
  [PurposeOfPayout.UTILITY_PROVIDER]: l('payouts.create.purpose.utilityProvider'),
  [PurposeOfPayout.TAXES]: l('payouts.create.purpose.taxes'),
  [PurposeOfPayout.TOURISM_EXPENSE]: l('payouts.create.purpose.tourismExpense'),
  [PurposeOfPayout.TRAVEL]: l('payouts.create.purpose.travel'),
  [PurposeOfPayout.SAVINGS_RETIREMENT]: l('payouts.create.purpose.savingsRetirement'),
  [PurposeOfPayout.PENSION]: l('payouts.create.purpose.pension'),
  [PurposeOfPayout.PAYROLL]: l('payouts.create.purpose.payroll'),
  [PurposeOfPayout.PHYSICAL_GOODS]: l('payouts.create.purpose.physicalGoods'),
  [PurposeOfPayout.DIGITAL_GOODS]: l('payouts.create.purpose.digitalGoods'),
  [PurposeOfPayout.SUPPLIER]: l('payouts.create.purpose.supplier'),
  [PurposeOfPayout.CAPITAL_CONTRIBUTION]: l('payouts.create.purpose.capitalContribution'),
  [PurposeOfPayout.PROPERTY_RENTAL]: l('payouts.create.purpose.propertyRental'),
  [PurposeOfPayout.LOGISTICS]: l('payouts.create.purpose.logistics'),
  [PurposeOfPayout.INVESTMENT_INTEREST]: l('payouts.create.purpose.investmentInterest'),
  [PurposeOfPayout.AVAILABILITY]: l('payouts.create.purpose.availability'),
  [PurposeOfPayout.INSURANCE]: l('payouts.create.purpose.insurance'),
  [PurposeOfPayout.REWARDS]: l('payouts.create.purpose.rewards'),
  [PurposeOfPayout.ENTERTAINMENT]: l('payouts.create.purpose.entertainment'),
  [PurposeOfPayout.PUBLIC_SERVICES]: l('payouts.create.purpose.publicServices'),
  [PurposeOfPayout.AFFILIATION_PROGRAMS]: l('payouts.create.purpose.affiliationPrograms'),
  [PurposeOfPayout.REMITTANCES]: l('payouts.create.purpose.remittances'),
  [PurposeOfPayout.OTHER_SERVICES]: l('payouts.create.purpose.otherServices'),
};

export const getPayoutWizardMenuItems = (activeStep: 'account' | 'payout' | 'review') => [
  {
    title: l('payouts.create.menu.accountData'),
    completed: activeStep === 'payout' || activeStep === 'review',
    selected: activeStep === 'account',
  },
  {
    title: l('payouts.create.menu.payoutData'),
    completed: activeStep === 'review',
    selected: activeStep === 'payout',
  },
  {
    title: l('payouts.create.menu.review'),
    completed: false,
    selected: activeStep === 'review',
  },
];

export const getFlowTypeLabel = (flowType?: FlowType) => {
  if (flowType === FlowType.B2B) {
    return l('payouts.create.flow.business');
  }
  return l('payouts.create.flow.person');
};

export const getFieldLabel = (field: RequiredField) => {
  const map: Record<string, string> = {
    'beneficiary.first_name': l('payouts.create.field.firstName'),
    'beneficiary.last_name': l('payouts.create.field.lastName'),
    'beneficiary.email': l('payouts.create.field.email'),
    purpose: l('payouts.create.field.purpose'),
    'beneficiary.bank_account.code': l('payouts.create.field.bankName'),
    'beneficiary.bank_account.type': l('payouts.create.field.accountType'),
    'beneficiary.bank_account.account': l('payouts.create.field.accountNumber'),
    'beneficiary.document.id': l('payouts.create.field.document'),
    'beneficiary.document.type': l('payouts.create.field.documentType'),
    'beneficiary.bank_account.branch': l('payouts.create.field.bankBranch'),
    'beneficiary.address.street': l('payouts.create.field.beneficiaryAddress'),
    'beneficiary.business_name': l('payouts.create.field.businessName'),
    'beneficiary.address.city': l('payouts.create.field.beneficiaryAddressCity'),
    'remitter.first_name': l('payouts.create.field.remitterFirstName'),
    'remitter.last_name': l('payouts.create.field.remitterLastName'),
    'remitter.document.id': l('payouts.create.field.remitterDocument'),
    'remitter.nationality': l('payouts.create.field.remitterNationality'),
    'remitter.birth_info.date': l('payouts.create.field.remitterBirthDate'),
  };

  if (map[field.fieldPath]) {
    return map[field.fieldPath];
  }

  const fallback = field.fieldPath.split('.').pop() || field.fieldPath;
  return fallback.replace(/_/g, ' ').replace(/\b\w/g, character => character.toUpperCase());
};

const fieldPathToCreatePayoutDataKeyMap: Record<string, keyof CreatePayoutData> = {
  amount: 'transferAmount',
  country: 'transferCountry',
  flow_type: 'flowType',
  purpose: 'purpose',
  'beneficiary.purpose': 'purpose',
  'beneficiary.first_name': 'beneficiaryFirstName',
  'beneficiary.last_name': 'beneficiaryLastName',
  'beneficiary.business_name': 'businessName',
  'beneficiary.document.id': 'beneficiaryDocument',
  'beneficiary.document.type': 'beneficiaryDocumentType',
  'beneficiary.address.street': 'beneficiaryAddressStreet',
  'beneficiary.address.city': 'beneficiaryAddressCity',
  'beneficiary.bank_account.code': 'bankCode',
  'beneficiary.bank_name': 'bankCode',
  'beneficiary.bank_account.account': 'bankAccount',
  'beneficiary.bank_account.branch': 'bankBranch',
  'beneficiary.bank_account.type': 'bankAccountType',
  currency_to_pay: 'currencyToPay',
  description: 'description',
  notification_url: 'notificationUrl',
};

export const getCreatePayoutDataValueByFieldPath = (createPayoutData: Partial<CreatePayoutData>, fieldPath: string) => {
  const key = fieldPathToCreatePayoutDataKeyMap[fieldPath];
  if (!key) {
    return undefined;
  }
  return createPayoutData[key];
};

export const mapFieldPathValuesToCreatePayoutData = (values: Record<string, string | number>) => {
  return Object.entries(values).reduce<Partial<CreatePayoutData>>((accumulator, [fieldPath, value]) => {
    const key = fieldPathToCreatePayoutDataKeyMap[fieldPath];
    if (key) {
      accumulator[key] = value as never;
    }
    return accumulator;
  }, {});
};

const createPayoutDataKeyToFieldPathMap = Object.entries(fieldPathToCreatePayoutDataKeyMap).reduce<
  Partial<Record<keyof CreatePayoutData, string>>
>((accumulator, [fieldPath, key]) => {
  if (!accumulator[key]) {
    accumulator[key] = fieldPath;
  }
  return accumulator;
}, {});

export const mapCreatePayoutDataToFieldPathValues = (createPayoutData: Partial<CreatePayoutData>) => {
  return Object.entries(createPayoutData).reduce<Record<string, string | number>>((accumulator, [rawKey, value]) => {
    if (value === undefined) {
      return accumulator;
    }

    const key = rawKey as keyof CreatePayoutData;
    const fieldPath = createPayoutDataKeyToFieldPathMap[key];
    if (fieldPath) {
      accumulator[fieldPath] = value as string | number;
    }
    return accumulator;
  }, {});
};
